import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Download,
  Print,
  CheckCircle,
  Cancel,
  Warning,
  Person,
  Business,
  DateRange,
  Euro,
  LocationOn,
  Description,
  Assignment
} from '@mui/icons-material';
import api from '../services/api';

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signDialog, setSignDialog] = useState({ open: false, type: '' });
  const [signDate, setSignDate] = useState('');

  useEffect(() => {
    fetchContractDetails();
  }, [id]);

  const fetchContractDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/contracts/${id}`);
      setContract(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching contract:', error);
      setError('Failed to load contract details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getContractStatus = () => {
    if (!contract) return { status: 'unknown', color: 'default' };
    
    if (contract.status === 'terminated') {
      return { status: 'Terminated', color: 'error', icon: <Cancel /> };
    }
    
    if (!contract.signed_by_employee || !contract.signed_by_employer) {
      return { status: 'Pending Signature', color: 'warning', icon: <Warning /> };
    }
    
    const endDate = new Date(contract.end_date);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    
    if (endDate < today) {
      return { status: 'Expired', color: 'error', icon: <Cancel /> };
    }
    
    if (daysUntilExpiry <= 30) {
      return { status: `Expiring Soon (${daysUntilExpiry} days)`, color: 'warning', icon: <Warning /> };
    }
    
    return { status: 'Active', color: 'success', icon: <CheckCircle /> };
  };

  const calculateDuration = () => {
    if (!contract) return '';
    const start = new Date(contract.start_date);
    const end = new Date(contract.end_date);
    const months = Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 30));
    return `${months} months`;
  };

  const handleSign = async (type) => {
    try {
      const endpoint = type === 'employee' ? 'sign-employee' : 'sign-employer';
      await api.post(`/contracts/${id}/${endpoint}`, { signed_date: signDate });
      
      await fetchContractDetails();
      setSignDialog({ open: false, type: '' });
      setSignDate('');
    } catch (error) {
      console.error('Error signing contract:', error);
      setError('Failed to sign contract');
    }
  };

  const handleTerminate = async () => {
    if (window.confirm('Are you sure you want to terminate this contract?')) {
      try {
        await api.post(`/contracts/${id}/terminate`);
        await fetchContractDetails();
      } catch (error) {
        console.error('Error terminating contract:', error);
        setError('Failed to terminate contract');
      }
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading contract details...</Typography>
      </Container>
    );
  }

  if (error || !contract) {
    return (
      <Container>
        <Alert severity="error">{error || 'Contract not found'}</Alert>
        <Button onClick={() => navigate('/admin/contracts')} sx={{ mt: 2 }}>
          Back to Contracts
        </Button>
      </Container>
    );
  }

  const contractStatus = getContractStatus();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin/contracts')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">
              Contract: {contract.contract_number || `CNT-${contract.id}`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              {contractStatus.icon}
              <Chip 
                label={contractStatus.status}
                color={contractStatus.color}
                size="small"
              />
              <Chip 
                label={contract.contract_type}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            disabled
            title="PDF generation coming soon"
          >
            Download PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            disabled
            title="Print functionality coming soon"
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/admin/contracts/edit/${id}`)}
          >
            Edit Contract
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Employee Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Person color="primary" />
                <Typography variant="h6">Employee Information</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                    }}
                    onClick={() => navigate(`/admin/employees/${contract.employee_id}`)}
                  >
                    {contract.employee_first_name} {contract.employee_last_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Personal Code
                  </Typography>
                  <Typography variant="body1">
                    {contract.employee_personal_code}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Position
                  </Typography>
                  <Typography variant="body1">
                    {contract.position_title}
                  </Typography>
                  {contract.position_title_lt && (
                    <Typography variant="body2" color="text.secondary">
                      {contract.position_title_lt}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Work Location
                  </Typography>
                  <Typography variant="body1">
                    {contract.work_location}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {contract.work_location_country}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Contract Terms */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DateRange color="primary" />
                <Typography variant="h6">Contract Terms</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(contract.start_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(contract.end_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body1">
                    {calculateDuration()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Probation Period
                  </Typography>
                  <Typography variant="body1">
                    {contract.probation_period_months || 0} months
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Working Hours
                  </Typography>
                  <Typography variant="body1">
                    {contract.working_hours_per_week} hours/week
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Vacation Days
                  </Typography>
                  <Typography variant="body1">
                    {contract.vacation_days} days/year
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Compensation */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Euro color="primary" />
                <Typography variant="h6">Compensation</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(contract.salary_amount, contract.salary_currency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {contract.salary_type}
                  </Typography>
                </Grid>
                {contract.salary_type === 'hourly' && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Estimated Monthly
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(
                        contract.salary_amount * contract.working_hours_per_week * 4.33,
                        contract.salary_currency
                      )}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Signature Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Assignment color="primary" />
                <Typography variant="h6">Signature Status</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {contract.signed_by_employee ? (
                      <CheckCircle color="success" />
                    ) : (
                      <Cancel color="disabled" />
                    )}
                    <Typography>Employee Signature</Typography>
                  </Box>
                  {contract.signed_by_employee ? (
                    <Typography variant="body2" color="text.secondary">
                      Signed on {formatDate(contract.employee_signed_date)}
                    </Typography>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => {
                        setSignDate(new Date().toISOString().split('T')[0]);
                        setSignDialog({ open: true, type: 'employee' });
                      }}
                    >
                      Mark as Signed
                    </Button>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {contract.signed_by_employer ? (
                      <CheckCircle color="success" />
                    ) : (
                      <Cancel color="disabled" />
                    )}
                    <Typography>Employer Signature</Typography>
                  </Box>
                  {contract.signed_by_employer ? (
                    <Typography variant="body2" color="text.secondary">
                      Signed on {formatDate(contract.employer_signed_date)}
                    </Typography>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => {
                        setSignDate(new Date().toISOString().split('T')[0]);
                        setSignDialog({ open: true, type: 'employer' });
                      }}
                    >
                      Mark as Signed
                    </Button>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Client Company (if exists) */}
        {contract.client_company_name && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Business color="primary" />
                  <Typography variant="h6">Client Company</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Company Name
                    </Typography>
                    <Typography variant="body1">
                      {contract.client_company_name}
                    </Typography>
                  </Grid>
                  {contract.client_company_code && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Company Code
                      </Typography>
                      <Typography variant="body1">
                        {contract.client_company_code}
                      </Typography>
                    </Grid>
                  )}
                  {contract.client_company_address && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1">
                        {contract.client_company_address}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Notes */}
        {contract.notes && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Description color="primary" />
                  <Typography variant="h6">Notes</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                  {contract.notes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Actions */}
        {contractStatus.status === 'Active' && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Contract Actions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleTerminate}
                  >
                    Terminate Contract
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Sign Dialog */}
      <Dialog open={signDialog.open} onClose={() => setSignDialog({ open: false, type: '' })}>
        <DialogTitle>
          Mark as Signed by {signDialog.type === 'employee' ? 'Employee' : 'Employer'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Signature Date"
            type="date"
            value={signDate}
            onChange={(e) => setSignDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignDialog({ open: false, type: '' })}>
            Cancel
          </Button>
          <Button onClick={() => handleSign(signDialog.type)} variant="contained">
            Confirm Signature
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContractDetail;
